import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { toast } from "sonner";
import { Plus, Trash2, FileCheck2, Loader2 } from "lucide-react";

const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;
const today = () => new Date().toISOString().slice(0, 10);
const emptyItem = () => ({ product: "", qty: 1, unit_price: "" });

export default function PendingSalesPanel({ onChange }) {
  const { user } = useAuth();
  const canManage = ["admin", "manager"].includes(user?.role);
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("aperto");
  const [recipes, setRecipes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: today(), customer: "", notes: "", items: [emptyItem()] });
  const [selected, setSelected] = useState([]);
  const [invOpen, setInvOpen] = useState(false);
  const [inv, setInv] = useState({ date: today(), payment_method: "banca", tax_rate: 10, description: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/pending-sales", { params: { status: status === "all" ? "" : status } }); setRows(data); setSelected([]); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/food-costs").then(({ data }) => setRecipes(data)).catch(() => {}); }, []);

  const setItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, k) => (k === i ? { ...it, ...patch } : it)) }));
  const pickRecipe = (i, name) => {
    const r = recipes.find((x) => x.name === name);
    setItem(i, { product: name, unit_price: r?.selling_price || "" });
  };
  const total = form.items.reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0), 0);

  const save = async () => {
    const items = form.items.filter((it) => it.product.trim()).map((it) => ({ product: it.product.trim(), qty: parseFloat(it.qty) || 0, unit_price: parseFloat(it.unit_price) || 0 }));
    if (!items.length) return toast.error("Inserisci almeno un piatto");
    setBusy(true);
    try {
      const { data } = await api.post("/pending-sales", { ...form, items });
      toast.success(`Uscita registrata · scaricati ${data.deductions?.deducted ?? 0} piatti dal magazzino`);
      setOpen(false); setForm({ date: today(), customer: "", notes: "", items: [emptyItem()] });
      load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const invoice = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pending-sales/invoice", { ids: selected, ...inv, tax_rate: parseFloat(inv.tax_rate) || 0 });
      toast.success(`Fattura registrata: ${eur(data.total)} (${data.count} uscite) come incasso ufficiale`);
      setInvOpen(false); load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare l'uscita? Gli ingredienti verranno ricaricati in magazzino.")) return;
    try { await api.delete(`/pending-sales/${id}`); toast.success("Eliminata"); load(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const selTotal = (rows || []).filter((r) => selected.includes(r.id)).reduce((s, r) => s + (r.total || 0), 0);
  const customers = [...new Set((rows || []).map((r) => r.customer).filter(Boolean))];

  return (
    <div data-testid="pending-sales-panel">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-44" data-testid="pending-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="aperto">Da fatturare</SelectItem><SelectItem value="fatturato">Fatturati</SelectItem><SelectItem value="all">Tutti</SelectItem></SelectContent>
        </Select>
        {canManage && status === "aperto" && customers.map((c) => (
          <Button key={c} size="sm" variant="outline" className="rounded-full" onClick={() => setSelected(rows.filter((r) => r.customer === c).map((r) => r.id))} data-testid={`select-customer-${c.replace(/\W+/g, "-")}`}>Seleziona {c}</Button>
        ))}
        <div className="ml-auto flex gap-2">
          {canManage && selected.length > 0 && (
            <Button className="rounded-full" onClick={() => setInvOpen(true)} data-testid="invoice-selected-button"><FileCheck2 className="w-4 h-4 mr-2" /> Fattura emessa ({selected.length}) · {eur(selTotal)}</Button>
          )}
          <Button className="rounded-full" variant={selected.length ? "outline" : "default"} onClick={() => setOpen(true)} data-testid="new-pending-button"><Plus className="w-4 h-4 mr-2" /> Nuova uscita</Button>
        </div>
      </div>

      <Card className="rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>{canManage && status === "aperto" && <TableHead className="w-8"></TableHead>}<TableHead>Data</TableHead><TableHead>Cliente</TableHead><TableHead>Piatti</TableHead><TableHead className="text-right">Totale</TableHead><TableHead>Stato</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {!rows && <TableRow><TableCell colSpan={7}><Loader2 className="w-4 h-4 animate-spin" /></TableCell></TableRow>}
              {rows?.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nessuna uscita in sospeso.</TableCell></TableRow>}
              {(rows || []).map((r) => (
                <TableRow key={r.id} data-testid={`pending-row-${r.id}`}>
                  {canManage && status === "aperto" && <TableCell><Checkbox checked={selected.includes(r.id)} onCheckedChange={(v) => setSelected(v ? [...selected, r.id] : selected.filter((x) => x !== r.id))} data-testid={`pending-check-${r.id}`} /></TableCell>}
                  <TableCell className="whitespace-nowrap">{r.date}</TableCell>
                  <TableCell className="font-medium">{r.customer || "—"}</TableCell>
                  <TableCell className="text-sm">{(r.items || []).map((it) => `${it.qty}× ${it.product}`).join(", ")}</TableCell>
                  <TableCell className="text-right font-medium">{eur(r.total)}</TableCell>
                  <TableCell><Badge variant="secondary" className={`rounded-full ${r.status === "aperto" ? "bg-amber-500/15 text-amber-700" : "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]"}`}>{r.status === "aperto" ? "Da fatturare" : `Fatturato ${r.invoice_date || ""}`}</Badge></TableCell>
                  <TableCell className="text-right">{canManage && r.status === "aperto" && <Button size="icon" variant="ghost" onClick={() => remove(r.id)} data-testid={`pending-delete-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="pending-dialog">
          <DialogHeader><DialogTitle>Nuova uscita piatti in sospeso</DialogTitle><DialogDescription>Scarica subito gli ingredienti dal magazzino; l'incasso resta in attesa della fattura mensile.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="pending-date" /></div>
            <div className="space-y-1.5"><Label>Cliente / Azienda</Label><Input value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} placeholder="es. Ditta Rossi" data-testid="pending-customer" /></div>
          </div>
          <datalist id="recipe-names">{recipes.map((r) => <option key={r.id} value={r.name} />)}</datalist>
          <div className="space-y-2 mt-2">
            <Label>Piatti</Label>
            {form.items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <Input list="recipe-names" className="col-span-6" placeholder="Piatto (dal Food Cost)" value={it.product} onChange={(e) => pickRecipe(i, e.target.value)} data-testid={`pending-item-product-${i}`} />
                <Input type="number" inputMode="decimal" className="col-span-2" placeholder="Q.tà" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} data-testid={`pending-item-qty-${i}`} />
                <Input type="number" inputMode="decimal" className="col-span-3" placeholder="€ unit." value={it.unit_price} onChange={(e) => setItem(i, { unit_price: e.target.value })} data-testid={`pending-item-price-${i}`} />
                <Button size="icon" variant="ghost" className="col-span-1" onClick={() => setForm({ ...form, items: form.items.filter((_, k) => k !== i) })}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })} data-testid="pending-add-item"><Plus className="w-4 h-4 mr-1" /> Aggiungi piatto</Button>
          </div>
          <div className="space-y-1.5 mt-2"><Label>Note</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="pending-notes" /></div>
          <DialogFooter className="items-center gap-3">
            <span className="text-sm text-muted-foreground mr-auto">Totale da fatturare: <b data-testid="pending-total">{eur(total)}</b></span>
            <Button onClick={save} disabled={busy} className="rounded-full" data-testid="pending-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registra uscita"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={invOpen} onOpenChange={setInvOpen}>
        <DialogContent data-testid="invoice-dialog">
          <DialogHeader><DialogTitle>Fattura emessa</DialogTitle><DialogDescription>Registra l'incasso ufficiale di {eur(selTotal)} per {selected.length} uscite selezionate.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Data fattura</Label><Input type="date" value={inv.date} onChange={(e) => setInv({ ...inv, date: e.target.value })} data-testid="invoice-date" /></div>
            <div className="space-y-1.5"><Label>Incassato su</Label>
              <Select value={inv.payment_method} onValueChange={(v) => setInv({ ...inv, payment_method: v })}>
                <SelectTrigger data-testid="invoice-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="banca">Banca</SelectItem><SelectItem value="contante">Contanti</SelectItem><SelectItem value="sumup">SumUp</SelectItem><SelectItem value="deposito">Deposito</SelectItem><SelectItem value="nessuno">Nessun conto</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>IVA %</Label><Input type="number" value={inv.tax_rate} onChange={(e) => setInv({ ...inv, tax_rate: e.target.value })} data-testid="invoice-tax" /></div>
            <div className="space-y-1.5"><Label>Descrizione (opz.)</Label><Input value={inv.description} onChange={(e) => setInv({ ...inv, description: e.target.value })} placeholder="Fattura n. …" data-testid="invoice-desc" /></div>
          </div>
          <DialogFooter><Button onClick={invoice} disabled={busy} className="rounded-full" data-testid="invoice-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registra incasso"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
